import React, { useEffect, useState } from "react";

const ReadyToPlanEditor = () => {
  const [formData, setFormData] = useState({
    id: 1,
    image: [],
    heading: "",
    text: "",
    button_text: "",
    button_link: "",
    status: "inactive",
  });

  const [previewImage, setPreviewImage] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    fetchReadyToPlan();
  }, []);

  const normalizeStatus = (value) => {
    if (
      value === "active" ||
      value === 1 ||
      value === "1" ||
      value === true ||
      value === "true"
    ) {
      return "active";
    }
    return "inactive";
  };

  const fetchReadyToPlan = async () => {
    try {
      setLoading(true);
      setError("");

      const response = await fetch(
        "https://eventscons.com/eventbackendnew/api/ready-to-plan-list"
      );

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const result = await response.json();
      const readyData =
        result?.status && Array.isArray(result?.data) && result.data.length > 0
          ? result.data[0]
          : null;

      if (readyData) {
        const currentStatus =
          readyData.status ??
          readyData.is_active ??
          readyData.ready_to_plan_status ??
          "inactive";

        setFormData({
          id: readyData.id || 1,
          image: readyData.image || [],
          heading: readyData.heading || "",
          text: readyData.text || "",
          button_text: readyData.button_text || "",
          button_link: readyData.button_link || "",
          status: normalizeStatus(currentStatus),
        });

        setPreviewImage(
          Array.isArray(readyData.image)
            ? readyData.image[0] || ""
            : readyData.image || ""
        );
      } else {
        throw new Error(result.message || "Failed to fetch Ready to Plan data");
      }
    } catch (err) {
      setError(err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];

    if (file) {
      const imageUrl = URL.createObjectURL(file);
      setPreviewImage(imageUrl);

      setFormData((prev) => ({
        ...prev,
        image: [file],
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setMessage("");
    setError("");

    try {
      const payload = new FormData();

      payload.append("id", formData.id);
      payload.append("heading", formData.heading || "");
      payload.append("text", formData.text || "");
      payload.append("button_text", formData.button_text || "");
      payload.append("button_link", formData.button_link || "");

      if (
        formData.image &&
        formData.image.length > 0 &&
        formData.image[0] instanceof File
      ) {
        payload.append("image", formData.image[0]);
      }

      const response = await fetch(
        "https://eventscons.com/eventbackendnew/api/ready-to-plan-update",
        {
          method: "POST",
          body: payload,
        }
      );

      const result = await response.json();

      if (!response.ok || result.status === false) {
        throw new Error(result.message || "Ready to Plan update failed");
      }

      setMessage(result.message || "Ready to Plan updated successfully");
      await fetchReadyToPlan();
    } catch (err) {
      setError(err.message || "Something went wrong while updating");
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusChange = async (e) => {
    const selectedStatus = e.target.value;
    const previousStatus = formData.status;

    setFormData((prev) => ({
      ...prev,
      status: selectedStatus,
    }));

    try {
      setStatusLoading(true);
      setMessage("");
      setError("");

      const response = await fetch(
        `https://eventscons.com/eventbackendnew/api/ready-to-status?status=${encodeURIComponent(
          selectedStatus
        )}`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
          },
        }
      );

      const result = await response.json();

      if (!response.ok || result.status === false) {
        throw new Error(result.message || "Status update failed");
      }

      setMessage(result.message || `Status changed to ${selectedStatus}`);
    } catch (err) {
      setError(err.message || "Something went wrong while updating status");

      setFormData((prev) => ({
        ...prev,
        status: previousStatus,
      }));
    } finally {
      setStatusLoading(false);
    }
  };

  if (loading) {
    return <p style={styles.loading}>Loading Ready to Plan details...</p>;
  }

  if (error && !formData.heading) {
    return <p style={styles.error}>{error}</p>;
  }

  return (
    <section style={styles.section}>
      <div style={styles.container}>
        <div style={styles.topBar}>
          <div>
            <h2 style={styles.heading}>Ready to Plan Section</h2>
          </div>

          <div style={styles.statusCard}>
            <div
              style={{
                ...styles.selectWrap,
                borderColor:
                  formData.status === "active" ? "#22c55e" : "#d1d5db",
                background:
                  formData.status === "active"
                    ? "linear-gradient(135deg, #f0fdf4, #dcfce7)"
                    : "linear-gradient(135deg, #ffffff, #f9fafb)",
              }}
            >
              <span
                style={{
                  ...styles.statusDot,
                  background:
                    formData.status === "active" ? "#22c55e" : "#9ca3af",
                  boxShadow:
                    formData.status === "active"
                      ? "0 0 0 4px rgba(34,197,94,0.12)"
                      : "0 0 0 4px rgba(156,163,175,0.12)",
                }}
              />

              <select
                value={formData.status}
                onChange={handleStatusChange}
                style={styles.select}
                disabled={statusLoading}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.imageBox}>
            {previewImage ? (
              <img
                src={previewImage}
                alt="Ready to Plan Preview"
                style={styles.image}
              />
            ) : (
              <div style={styles.noImageBox}>No image selected</div>
            )}

            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              style={styles.input}
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Heading</label>
            <input
              type="text"
              name="heading"
              value={formData.heading}
              onChange={handleChange}
              style={styles.input}
              placeholder="Enter heading"
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Text</label>
            <textarea
              name="text"
              value={formData.text}
              onChange={handleChange}
              rows="5"
              style={styles.textarea}
              placeholder="Enter text"
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Button Text</label>
            <input
              type="text"
              name="button_text"
              value={formData.button_text}
              onChange={handleChange}
              style={styles.input}
              placeholder="Enter button text"
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Button Link</label>
            <input
              type="text"
              name="button_link"
              value={formData.button_link}
              onChange={handleChange}
              style={styles.input}
              placeholder="Enter button link"
            />
          </div>

          {statusLoading && <p style={styles.info}>Updating status...</p>}
          {message && <p style={styles.success}>{message}</p>}
          {error && <p style={styles.error}>{error}</p>}

          <button type="submit" style={styles.button} disabled={submitting}>
            {submitting ? "Saving..." : "Update Ready to Plan"}
          </button>
        </form>
      </div>
    </section>
  );
};

const styles = {
  section: {
    padding: "32px",
    background: "linear-gradient(180deg, #ffffff 0%, #fafafa 100%)",
    margin: "30px",
    borderRadius: "24px",
    border: "1px solid #ececec",
    boxShadow: "0 20px 45px rgba(15, 23, 42, 0.08)",
  },
  container: {
    maxWidth: "1040px",
    margin: "0 auto",
  },
  topBar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "20px",
    marginBottom: "28px",
    flexWrap: "wrap",
    paddingBottom: "20px",
    borderBottom: "1px solid #f1f1f1",
  },
  heading: {
    margin: 0,
    fontSize: "30px",
    fontWeight: "800",
    color: "#111827",
    letterSpacing: "-0.4px",
    textAlign: "left",
  },
  statusCard: {},
  selectWrap: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    border: "1px solid #d1d5db",
    borderRadius: "14px",
    padding: "0 14px",
    minHeight: "50px",
    position: "relative",
    transition: "all 0.25s ease",
    minWidth: "220px",
  },
  statusDot: {
    width: "10px",
    height: "10px",
    borderRadius: "50%",
    flexShrink: 0,
  },
  select: {
    flex: 1,
    border: "none",
    outline: "none",
    background: "transparent",
    fontSize: "15px",
    fontWeight: "700",
    color: "#111827",
    cursor: "pointer",
    appearance: "none",
    WebkitAppearance: "none",
    MozAppearance: "none",
    padding: "12px 26px 12px 0",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "22px",
  },
  imageBox: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    padding: "18px",
    background: "#fcfcfc",
    border: "1px solid #eeeeee",
    borderRadius: "18px",
  },
  image: {
    width: "100%",
    maxWidth: "620px",
    height: "300px",
    objectFit: "cover",
    borderRadius: "16px",
    border: "1px solid #e5e7eb",
    boxShadow: "0 12px 24px rgba(0,0,0,0.08)",
  },
  noImageBox: {
    width: "100%",
    maxWidth: "620px",
    height: "240px",
    border: "1.5px dashed #cbd5e1",
    borderRadius: "16px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#6b7280",
    background: "linear-gradient(180deg, #f9fafb 0%, #f3f4f6 100%)",
    fontWeight: "600",
    fontSize: "15px",
  },
  field: {
    display: "flex",
    flexDirection: "column",
    gap: "9px",
  },
  label: {
    fontWeight: "700",
    color: "#1f2937",
    fontSize: "14px",
  },
  input: {
    padding: "14px 16px",
    borderRadius: "14px",
    border: "1px solid #d1d5db",
    fontSize: "15px",
    width: "100%",
    outline: "none",
    background: "#fff",
    color: "#111827",
    boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
    boxSizing: "border-box",
  },
  textarea: {
    padding: "14px 16px",
    borderRadius: "14px",
    border: "1px solid #d1d5db",
    fontSize: "15px",
    resize: "vertical",
    width: "100%",
    minHeight: "140px",
    outline: "none",
    background: "#fff",
    color: "#111827",
    boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
    boxSizing: "border-box",
  },
  button: {
    padding: "14px 22px",
    background: "linear-gradient(135deg, #9B1321 0%, #7f0f1b 100%)",
    color: "#fff",
    border: "none",
    borderRadius: "14px",
    fontSize: "15px",
    fontWeight: "700",
    cursor: "pointer",
    width: "fit-content",
    boxShadow: "0 12px 20px rgba(155, 19, 33, 0.25)",
  },
  success: {
    color: "#166534",
    background: "#ecfdf5",
    border: "1px solid #bbf7d0",
    padding: "12px 14px",
    borderRadius: "12px",
    fontWeight: "700",
  },
  error: {
    color: "#991b1b",
    background: "#fef2f2",
    border: "1px solid #fecaca",
    padding: "12px 14px",
    borderRadius: "12px",
    fontWeight: "700",
  },
  info: {
    color: "#1d4ed8",
    background: "#eff6ff",
    border: "1px solid #bfdbfe",
    padding: "12px 14px",
    borderRadius: "12px",
    fontWeight: "700",
  },
  loading: {
    padding: "40px",
    textAlign: "center",
    color: "#374151",
    fontWeight: "600",
  },
};

export default ReadyToPlanEditor;